import React, { useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { BookOpen, ChevronDown, Search, BookA, Home } from "lucide-react";
import { useKnowledge } from "../../lib/knowledge.jsx";
import { AreaIcon, StatusIcon } from "./shared";
import { cn } from "../../lib/utils";

export function SearchBox({ autoFocus, testid = "kc-search", className, onDone }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  return (
    <form role="search" className={cn("flex items-center gap-2 h-9 px-3 bg-surface-2 border border-border rounded-sm focus-within:border-primary/60 transition-colors", className)}
          onSubmit={e => { e.preventDefault(); if (q.trim()) { nav(`/learn/search?q=${encodeURIComponent(q.trim())}`); onDone?.(); } }}>
      <Search size={13} className="text-muted shrink-0" />
      <input data-testid={`${testid}-input`} value={q} onChange={e => setQ(e.target.value)} autoFocus={autoFocus}
             placeholder="Search topics, terms, tags…" aria-label="Search the Knowledge Center"
             className="bg-transparent outline-none w-full text-[13px] placeholder:text-subtle" />
    </form>
  );
}

function Area({ area, current, onPick }) {
  const { isDone, progress } = useKnowledge();
  const inArea = area.groups.some(g => g.topics.some(t => t.slug === current));
  const [open, setOpen] = useState(true);
  const done = progress?.areas.find(a => a.id === area.id)?.done ?? 0;
  return (
    <div data-testid={`kc-tree-area-${area.id}`} className="py-1">
      <button onClick={() => setOpen(o => !o)} data-testid={`kc-tree-area-${area.id}-toggle`} aria-expanded={open}
              className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-left rounded-sm hover:bg-surface-2 transition-colors", inArea && "text-primary")}>
        <AreaIcon icon={area.icon} size={14} className="shrink-0" />
        <span className="flex-1 text-[12.5px] font-semibold leading-tight">{area.title}</span>
        <span className="text-[10px] font-mono-tabular text-muted">{done}/{area.total}</span>
        <ChevronDown size={12} className={cn("text-muted transition-transform", !open && "-rotate-90")} />
      </button>
      {open && area.groups.map(g => (
        <div key={g.title} className="ml-3 pl-3 border-l border-border mt-1 mb-2">
          <div className="text-[9.5px] uppercase tracking-[0.18em] text-subtle font-semibold px-2 pt-1.5 pb-1">{g.title}</div>
          {g.topics.map(t => (
            <NavLink key={t.slug} to={`/learn/${t.slug}`} onClick={onPick} data-testid={`kc-tree-${t.slug}`}
                     className={({ isActive }) => cn("flex items-start gap-2 px-2 py-1.5 rounded-sm text-[12.5px] leading-snug transition-colors",
                       isActive ? "bg-primary/10 text-primary font-medium" : "text-fg/80 hover:bg-surface-2 hover:text-fg")}>
              <span className="mt-[2px]"><StatusIcon done={isDone(t.slug)} active={t.slug === current} size={12} /></span>
              <span>{t.title}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </div>
  );
}

// Left-hand topic tree: home, search, the four learning areas and the glossary.
export default function TopicTree({ onPick }) {
  const { index, progress } = useKnowledge();
  const { slug } = useParams();
  return (
    <nav aria-label="Knowledge Center topics" data-testid="kc-tree" className="space-y-3">
      <Link to="/learn" onClick={onPick} data-testid="kc-tree-home" className="flex items-center gap-2.5 group">
        <span className="w-8 h-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center border border-primary/25"><BookOpen size={15} /></span>
        <span className="leading-tight">
          <span className="block font-display font-semibold text-[15px] tracking-tight group-hover:text-primary transition-colors">Knowledge Center</span>
          <span className="block text-[10px] text-muted font-mono-tabular">{progress ? `${progress.done} of ${progress.total} topics complete` : "1Rotary Control Tower"}</span>
        </span>
      </Link>
      <SearchBox onDone={onPick} testid="kc-tree-search" />
      <div className="flex gap-1.5 text-[11.5px]">
        <Link to="/learn" onClick={onPick} className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded-sm hover:border-primary/50 hover:text-primary transition-colors" data-testid="kc-tree-home-link"><Home size={11} />Home</Link>
        <Link to="/learn/glossary" onClick={onPick} className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded-sm hover:border-primary/50 hover:text-primary transition-colors" data-testid="kc-tree-glossary"><BookA size={11} />Glossary</Link>
      </div>
      <div className="pt-1">{index?.areas.map(a => <Area key={a.id} area={a} current={slug} onPick={onPick} />)}</div>
    </nav>
  );
}
